/**
 * Server-rendered first-layer cookie bar: the pre-paint guard (mobile LCP).
 *
 * The root layout is static, so the server cannot know whether a visitor has
 * answered. CookieConsentBanner therefore renders BOTH first-layer bars (US and
 * restricted copy) into the server HTML, marked data-cookie-notice="prepaint",
 * and CONSENT_PREPAINT_CSS hides them by default. This script runs in <head>
 * before the body paints and, only when the bar may show, sets
 * data-consent-prompt on <html> to the visitor's region, which reveals that one
 * bar. On hydration the banner decides again exactly as before and keeps,
 * swaps out or removes the bar.
 *
 * Fail-safe: no JavaScript, a script error, GPC or any stored answer leaves the
 * attribute unset, so nothing shows before hydration. The script reads cookies
 * and storage only; it never writes them and never touches tags.
 *
 * The decision mirrors shouldShowConsentPrompt (consent-prompt.ts) fed the way
 * the banner's mount effect feeds it: readConsentCookie + parseConsentCookie +
 * parseConsentDecisionMeta for the stored answer, the rr_consent_prompt
 * lastShownAt, the rr_consent_session flag and navigator.globalPrivacyControl.
 * The region is consentRegionRestrictedFromCookieHeader (rr_cr, set httpOnly
 * false by middleware for this purpose). consent-prepaint.test.ts runs both
 * across the same inputs.
 */

import {
  CONSENT_REGION_COOKIE,
  CONSENT_REGION_UNRESTRICTED_VALUE,
} from '@/lib/analytics/consent-regions'
import { CONSENT_COOKIE } from '@/lib/identity/consent'
import {
  CONSENT_PROMPT_SESSION_KEY,
  CONSENT_PROMPT_STORAGE_KEY,
  CONSENT_PURPOSES_VERSION,
  MS_PER_DAY,
} from '@/lib/identity/consent-prompt'

/** Set on <html> by the guard: the region whose bar may paint before hydration. */
export const CONSENT_PROMPT_ATTR = 'data-consent-prompt'

/** data-cookie-notice value of a bar the server rendered, until the banner decides. */
export const CONSENT_PREPAINT_NOTICE = 'prepaint'

/**
 * Hide-only rule, copied verbatim into app/globals.css (pinned by the test).
 * Each server-rendered bar stays hidden unless <html> names its region.
 */
export const CONSENT_PREPAINT_CSS = `html:not([${CONSENT_PROMPT_ATTR}='us']) [data-cookie-notice='${CONSENT_PREPAINT_NOTICE}'][data-consent-region='us'],
html:not([${CONSENT_PROMPT_ATTR}='restricted']) [data-cookie-notice='${CONSENT_PREPAINT_NOTICE}'][data-consent-region='restricted'] {
  display: none;
}`

const js = (value: string | number) => JSON.stringify(value)

/**
 * The inline <head> script, as one ES5 string. Built from the shared constants
 * so a renamed cookie or key cannot drift from the banner.
 */
export function consentPrepaintScript(): string {
  return (
    `(function(){try{` +
    `var d=document,c=d.cookie||'',i,rows,raw,v,a,m,ver=null,ns=true,last=null,r;` +
    // GPC: never (shouldShowConsentPrompt).
    `if(navigator.globalPrivacyControl===true)return;` +
    // readConsentCookie: split('; '), first row starting KEY=, split('=')[1].
    `rows=c.split('; ');` +
    `for(i=0;i<rows.length;i++){if(rows[i].indexOf(${js(CONSENT_COOKIE + '=')})===0){raw=rows[i].split('=')[1];break}}` +
    // parseConsentCookie: empty is no answer; unreadable is a decline; bare `all` is an accept.
    `v=typeof raw==='string'?raw.trim():'';` +
    `if(v){` +
    `try{var p=JSON.parse(decodeURIComponent(v));a=Boolean(p.analytics);m=Boolean(p.marketing)}catch(e){a=m=v==='all'}` +
    // parseConsentDecisionMeta(raw).v: a finite number, else null.
    `try{var q=JSON.parse(decodeURIComponent(raw));ver=typeof q.v==='number'&&isFinite(q.v)?q.v:null}catch(e){ver=null}` +
    // An accept re-asks only for an older purposes version; a decline never does.
    `if(!(a||m)||!((ver==null?0:ver)<${js(CONSENT_PURPOSES_VERSION)}))return` +
    `}` +
    // Daily cap: a new tab session within 24h of the last showing stays quiet.
    `try{ns=sessionStorage.getItem(${js(CONSENT_PROMPT_SESSION_KEY)})!=='1'}catch(e){ns=true}` +
    `try{var b=localStorage.getItem(${js(CONSENT_PROMPT_STORAGE_KEY)});if(b){var o=JSON.parse(b);last=typeof o.lastShownAt==='number'&&isFinite(o.lastShownAt)?o.lastShownAt:null}}catch(e){last=null}` +
    `if(ns&&last!=null&&Date.now()-last<${js(MS_PER_DAY)})return;` +
    // consentRegionRestrictedFromCookieHeader: only rr_cr=0 is unrestricted.
    `rows=c?c.split(';'):[];` +
    `for(i=0;i<rows.length;i++){var s=rows[i].trim();if(s.indexOf(${js(CONSENT_REGION_COOKIE + '=')})===0){r=s.slice(${js(CONSENT_REGION_COOKIE.length + 1)});break}}` +
    `d.documentElement.setAttribute(${js(CONSENT_PROMPT_ATTR)},r===${js(CONSENT_REGION_UNRESTRICTED_VALUE)}?'us':'restricted')` +
    `}catch(e){}})()`
  )
}
